import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { Ref } from 'vue'
import type { StorageLike } from 'pinia-plugin-persistedstate'
import { sessionStorage } from '@shared/helpers/storage'
import useUserStore from '@shared/stores/useUserStore.js'
import {
  GOAL_KEY_OTHER,
  GOAL_OPTIONS,
} from '@shared/modules/assessment/config/selfTestOptions'

// An answer as the screen holds it: the option key plus the copy that was on
// screen when it was picked.
type Answer = {
  key: string
  label: string
}

// An answer as the API takes it. `text` is the copy the user saw, which the
// backend stores alongside the scored option.
type SubmittedAnswer = {
  key: string
  text: string
  language: string
  referenced_goal_key?: string
}

// The renewal_therapy_feedback questionnaire asks exactly one of these two
// questions — which one depends on whether a goal came in with the deep link.
type RenewalTherapyFeedback = {
  goal_achievement?: SubmittedAnswer
  therapy_helpfulness?: SubmittedAnswer
}

export type RenewalAssessmentSubmission = {
  pgic: { overall_change: SubmittedAnswer }
  renewalTherapyFeedback: RenewalTherapyFeedback
}

export const useRenewalAssessmentStore = defineStore(
  'renewalAssessment',
  () => {
    // State

    // The therapy goal the app passed as the self_test_goal query param at flow
    // entry. Null when it was absent.
    const goalKey: Ref<string | null> = ref(null)

    // Each answer is the chosen option key plus the copy that was on screen —
    // the API stores both.
    const overallChange: Ref<Answer | null> = ref(null)
    const goalAchievement: Ref<Answer | null> = ref(null)
    const therapyHelpfulness: Ref<Answer | null> = ref(null)

    // Getters

    // The goal's own copy, so the goal-achievement screen can name it back to
    // the user.
    const goalLabelKey = computed(
      () =>
        GOAL_OPTIONS.find((option) => option.value === goalKey.value)?.label ??
        null,
    )

    // Only a nameable goal earns the goal-achievement branch; without one the
    // flow asks the generic therapy-helpfulness question instead.
    const hasGoal = computed(
      () => goalLabelKey.value != null && goalKey.value !== GOAL_KEY_OTHER,
    )

    /**
     * The request body for submitRenewalAssessment, or null while an answer the
     * flow asked for is still missing.
     */
    const submission = computed<RenewalAssessmentSubmission | null>(() => {
      const language = useUserStore().lang
      const answer = (
        value: Answer | null,
        extra?: Partial<SubmittedAnswer>,
      ): SubmittedAnswer | null =>
        value == null
          ? null
          : { key: value.key, text: value.label, language, ...extra }

      const pgicAnswer = answer(overallChange.value)
      const feedbackAnswer = hasGoal.value
        ? answer(goalAchievement.value, {
            referenced_goal_key: goalKey.value ?? undefined,
          })
        : answer(therapyHelpfulness.value)

      if (pgicAnswer == null || feedbackAnswer == null) return null

      return {
        pgic: { overall_change: pgicAnswer },
        renewalTherapyFeedback: hasGoal.value
          ? { goal_achievement: feedbackAnswer }
          : { therapy_helpfulness: feedbackAnswer },
      }
    })

    // Actions

    /** Capture the entry query's goal, ignoring a blank value. */
    function setGoalKey(value: string | null | undefined) {
      goalKey.value = value == null || value === '' ? null : value
    }

    return {
      // State
      goalKey,
      overallChange,
      goalAchievement,
      therapyHelpfulness,
      // Getters
      hasGoal,
      goalLabelKey,
      submission,
      // Actions
      setGoalKey,
    }
  },
  {
    persist: {
      storage: sessionStorage as unknown as StorageLike,
    },
  },
)

export default useRenewalAssessmentStore
